import { SearchBox } from './search-box';
import { GiaLookupLink } from './gia-lookup-link';
import HomeConsult from '@/features/home/partials/ConsultGuest';
import React, { ReactNode, useEffect, useState } from 'react';
import { getAuth } from '@/libs/cookies';
import { Icon, Link, BellOutlineIcon, UserOutlineIcon } from '@/components';
import { PagePath } from '@/layouts/shared';
import { useProfile } from '@/contexts/profile';
import { useFetchTotalByTab } from '@/features/notifications/list/api';
import NextLink from 'next/link';
import dayjs from 'dayjs';
import { BUSINESS } from '@/constants/business';
import { JOURNAL_TOPICS, JournalArticle, NAV_ITEMS, PAGE_TOPICS, TOPIC_BY_SLUG, Topic, topicHref } from './const';

/* ------------------------------------------------------------------ */
/* Nhỏ gọn dùng chung                                                  */
/* ------------------------------------------------------------------ */

export const articleHref = (a: Pick<JournalArticle, 'slug'>) => `/article/${a.slug}/`;
const fmtDate = (d: string) => (dayjs(d).isValid() ? dayjs(d).format('DD/MM/YYYY') : '');

export const A = ({ href, className = '', children, current = false }: { href: string; className?: string; children: ReactNode; current?: boolean }) => (
  <NextLink href={href}>
    <a className={className} aria-current={current ? 'page' : undefined}>{children}</a>
  </NextLink>
);

const serif = 'tw-font-jr';
const wrap = 'tw-mx-auto tw-w-full tw-max-w-[1296px] tw-px-5 md:tw-px-8';

export const Eyebrow = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <p className={`tw-m-0 tw-text-[12px] tw-font-medium tw-uppercase tw-tracking-[0.08em] tw-text-jr-gold ${className}`}>
    {children}
  </p>
);

const H2 = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <h2 className={`${serif} tw-m-0 tw-text-[28px] md:tw-text-[40px] tw-font-normal tw-leading-[1.2] tw-text-jr-ink ${className}`}>
    {children}
  </h2>
);

const Arrow = () => <span aria-hidden="true">→</span>;

const TextLink = ({ href, children, className = '' }: { href: string; children: ReactNode; className?: string }) => (
  <A href={href} className={`tw-inline-flex tw-items-center tw-gap-2 tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-underline ${className}`}>
    {children}
    <Arrow />
  </A>
);

export const Button = ({
  href,
  tone = 'green',
  children,
  className = '',
}: {
  href: string;
  tone?: 'green' | 'gold' | 'plum' | 'light';
  children: ReactNode;
  className?: string;
}) => {
  const tones = {
    green: 'tw-bg-jr-green tw-text-white',
    gold: 'tw-bg-jr-gold tw-text-white',
    plum: 'tw-bg-jr-plum tw-text-white',
    light: 'tw-bg-white tw-text-jr-green tw-border tw-border-jr-line',
  };
  return (
    <A
      href={href}
      className={`tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-min-h-[48px] tw-px-7 tw-text-[15px] tw-font-medium hover:tw-opacity-90 ${tones[tone]} ${className}`}
    >
      {children}
      <Arrow />
    </A>
  );
};

/* ------------------------------------------------------------------ */
/* Khung trang: thanh trên, header, footer                             */
/* ------------------------------------------------------------------ */

/**
 * Góc tài khoản của header trang tin.
 * - đã đăng nhập: chuông thông báo (chấm đỏ khi có mới) + avatar/tên; chưa: icon người + "Đăng nhập"
 */
const AccountLink = ({ className = '' }: { className?: string }) => {
  const [isAuth, setIsAuth] = useState(false);
  const [totalNotification, setTotalNotification] = useState(0);
  const profile: any = useProfile();
  const { refetch: refetchTotalByTab } = useFetchTotalByTab({
    enabled: false,
    refetchOnWindowFocus: false,
    retry: false,
    onSuccess({ data }) {
      setTotalNotification(Object.values(data || {}).reduce((a: number, b: any) => a + (+b || 0), 0));
    },
  });
  useEffect(() => {
    let auth = false;
    try {
      auth = !!getAuth().isAuth;
    } catch (_e) {
      auth = false;
    }
    setIsAuth(auth);
    if (auth) refetchTotalByTab();
  }, [refetchTotalByTab]);

  const color = { color: '#38544B' };
  return (
    <div className={`tw-min-w-0 tw-items-center tw-gap-x-3 ${className}`}>
      {isAuth ? (
        <>
          <Link url={PagePath.NotificationsPage} className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center" title="Thông báo">
            <div className="tw-relative tw-w-6 tw-h-6">
              {totalNotification > 0 && (
                <span className="tw-w-2 tw-h-2 tw-bg-danger-main tw-rounded-full tw-absolute tw-right-0 tw-top-0 tw-z-10"></span>
              )}
              <Icon icon={<BellOutlineIcon />} style={color} />
            </div>
          </Link>
          <Link url={PagePath.AccountProfilePage} title={profile && profile.avatar ? 'Cá nhân' : 'Tài khoản'}>
            {profile && profile.avatar ? (
              <div className="tw-flex tw-items-center tw-gap-x-2.5">
                <div
                  className="tw-h-8 tw-w-8 tw-shrink-0 tw-rounded-full tw-border tw-border-jr-line tw-bg-center tw-bg-cover tw-bg-no-repeat"
                  style={{ backgroundImage: `url('${profile.avatar}')` }}
                />
                <span className="tw-max-w-[132px] tw-truncate tw-text-[14px] tw-font-medium" style={color}>
                  {profile.fullname}
                </span>
              </div>
            ) : (
              <div
                className="tw-h-8 tw-w-8 tw-rounded-full tw-bg-center tw-bg-contain tw-bg-no-repeat tw-border tw-border-jr-line"
                style={{ backgroundImage: `url(/logo.svg)` }}
              ></div>
            )}
          </Link>
        </>
      ) : (
        <Link url={PagePath.LoginPage} title="login" className="tw-flex tw-min-h-[40px] tw-items-center tw-gap-x-2" style={color}>
          <Icon icon={<UserOutlineIcon />} />
          <span className="tw-text-[14px] tw-font-medium">Đăng nhập</span>
        </Link>
      )}
    </div>
  );
};

export const JournalShell = ({ activeKey, children }: { activeKey?: string; query?: string; children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-bg-jr-bg tw-text-jr-ink tw-font-sans">
      <header className="tw-bg-[#F8F5ED] tw-relative tw-z-20">
        <div className={`${wrap} tw-grid tw-grid-cols-[minmax(0,1fr)_auto_auto] md:tw-grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] tw-items-center tw-gap-2 md:tw-gap-4 tw-h-[64px] md:tw-h-[76px]`}>
          <A href="/" className="tw-col-start-1 md:tw-col-start-2 tw-row-start-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.svg" alt="An Thư The Diamond Store" className="tw-h-[32px] md:tw-h-[50px] tw-w-auto" />
          </A>
          <GiaLookupLink className="tw-col-start-2 md:tw-col-start-1 tw-row-start-1 tw-justify-self-end md:tw-justify-self-start" />
          <AccountLink className="tw-col-start-3 tw-row-start-1 tw-justify-self-end tw-hidden lg:tw-inline-flex" />
          <div className="tw-col-start-3 tw-row-start-1 tw-justify-self-end tw-flex lg:tw-hidden tw-items-center">
            <button
              type="button"
              aria-label={open ? 'Đóng menu' : 'Mở menu'}
              aria-expanded={open}
              aria-controls="journal-mobile-menu"
              onClick={() => setOpen((v) => !v)}
              className="tw-flex tw-h-11 tw-w-11 tw-flex-col tw-items-center tw-justify-center tw-gap-[5px] tw-rounded-full tw-border tw-border-jr-line tw-bg-white focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-jr-green"
            >
              {open ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="tw-text-jr-green" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" /></svg>
              ) : <>
                <span className="tw-block tw-h-[2px] tw-w-5 tw-bg-jr-green" />
                <span className="tw-block tw-h-[2px] tw-w-5 tw-bg-jr-green" />
                <span className="tw-block tw-h-[2px] tw-w-5 tw-bg-jr-green" />
              </>}
            </button>
          </div>
        </div>
        <nav className="tw-hidden lg:tw-block tw-bg-jr-green" aria-label="Điều hướng chính">
          <div className={`${wrap} tw-flex tw-items-stretch tw-justify-between tw-gap-4 xl:tw-gap-8`}>
            {NAV_ITEMS.map((n) => (
              <A
                key={n.key}
                href={n.href}
                current={activeKey === n.key}
                className={`tw-flex tw-min-h-[52px] tw-items-center tw-justify-center tw-whitespace-nowrap tw-border-b-[3px] tw-px-2 xl:tw-px-3 tw-py-3 tw-text-[14px] xl:tw-text-[15px] tw-font-medium tw-transition-colors hover:tw-text-white hover:tw-border-[#D7BD80] focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-[-4px] focus-visible:tw-outline-[#D7BD80] ${
                  activeKey === n.key ? 'tw-text-white tw-border-[#D7BD80]' : 'tw-text-[#F3F0E7] tw-border-transparent'
                }`}
              >
                {n.label}
              </A>
            ))}
          </div>
        </nav>
        {open && (
          <div id="journal-mobile-menu" className="lg:tw-hidden tw-absolute tw-left-0 tw-right-0 tw-top-full tw-bg-jr-green tw-shadow-lg">
            <nav className={`${wrap} tw-py-2 tw-flex tw-flex-col`} aria-label="Điều hướng chính">
              {NAV_ITEMS.map((n) => (
                <A key={n.key} href={n.href} current={activeKey === n.key} className={`tw-flex tw-min-h-[52px] tw-items-center tw-border-b tw-border-b-white/20 tw-border-l-[3px] tw-px-4 tw-py-3 tw-text-[16px] tw-font-medium hover:tw-text-white focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-[-4px] focus-visible:tw-outline-[#D7BD80] ${activeKey === n.key ? 'tw-text-white tw-border-l-[#D7BD80]' : 'tw-text-[#F3F0E7] tw-border-l-transparent'}`}>
                  {n.label}
                </A>
              ))}
            </nav>
            <div className="tw-bg-[#F8F5ED]">
              <div className={`${wrap} tw-py-4`}><AccountLink className="tw-flex tw-text-[16px]" /></div>
            </div>
          </div>
        )}
      </header>

      <main>{children}</main>
      <JournalFooter />
    </div>
  );
};

export const Breadcrumb = ({ items, searchQuery = '' }: { items: { label: string; href?: string }[]; back?: boolean; searchQuery?: string }) => (
  <div className="tw-bg-[#F8F5ED] tw-border-b tw-border-solid tw-border-[#D8CFBC]">
    <div className={`${wrap} tw-flex tw-flex-col sm:tw-flex-row tw-items-stretch sm:tw-items-center tw-justify-between tw-gap-3 sm:tw-gap-6 tw-min-h-[60px] tw-py-2.5 tw-text-[13px] tw-text-jr-mute`}>
      <nav aria-label="Breadcrumb" className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-x-2">
        {items.map((it, i) => (
          <React.Fragment key={it.label}>
            {i > 0 && <span aria-hidden="true" className="tw-text-jr-gold/60">/</span>}
            {it.href ? (
              <A href={it.href} className="tw-text-jr-mute hover:tw-text-jr-green hover:tw-underline">
                {it.label}
              </A>
            ) : (
              <span className={i === items.length - 1 ? 'tw-font-medium tw-text-jr-green' : ''}>{it.label}</span>
            )}
          </React.Fragment>
        ))}
      </nav>
      <SearchBox id="journal-breadcrumb-search" defaultValue={searchQuery} className="tw-w-full sm:tw-w-[300px] lg:tw-w-[320px] tw-shrink-0" />
    </div>
  </div>
);

/** Khối tư vấn dùng chung với Home (design/AnThu-Home-TuVan): form 3 trường + chat guest, đã đăng nhập thì bấm tư vấn luôn. */
const ConsultBand = () => <HomeConsult variant="journal" />;

const JournalFooter = () => (
  <>
    <ConsultBand />
    <footer className="tw-bg-jr-ink tw-text-[#e8e4da]">
      <div className={`${wrap} tw-grid tw-gap-8 md:tw-grid-cols-4 tw-py-10 md:tw-py-12`}>
        <div>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo-white.svg" alt="An Thư" className="tw-h-[46px] tw-w-auto" />
          <p className="tw-mt-4 tw-mb-0 tw-text-[15px]">Trang sức kim cương tự nhiên</p>
        </div>
        <div className="tw-text-[15px] tw-leading-relaxed">
          <p className="tw-m-0 tw-text-[17px]">
            <a href="tel:0333336789" className="tw-text-inherit">Hotline: 033 333 6789</a>
          </p>
          <p className="tw-mt-3 tw-mb-0">89A Nguyễn Trãi, Phường Bến Thành, TP. Hồ Chí Minh</p>
          <p className="tw-mt-3 tw-mb-0">
            <A href="/" className="tw-text-inherit">anthu.vn</A>
          </p>
        </div>
        <ul className="tw-m-0 tw-p-0 tw-list-none tw-text-[15px] tw-leading-[2.2]">
          <li><A href="/ve-an-thu/" className="tw-text-inherit hover:tw-underline">Về An Thư</A></li>
          <li><A href="/shops/" className="tw-text-inherit hover:tw-underline">Hệ thống cửa hàng</A></li>
          <li><A href="/policies/product-warranty/" className="tw-text-inherit hover:tw-underline">Chính sách</A></li>
        </ul>
        <ul className="tw-m-0 tw-p-0 tw-list-none tw-text-[15px] tw-leading-[2]">
          {(
            [
              ['Facebook', BUSINESS.social.facebook],
              ['TikTok', BUSINESS.social.tiktok],
              ['YouTube', BUSINESS.social.youtube],
              ['Instagram', BUSINESS.social.instagram],
            ] as const
          ).map(([name, url]) => (
            <li key={name}>
              <a href={url} target="_blank" rel="noopener noreferrer" aria-label={`An Thư trên ${name}`} className="tw-text-inherit hover:tw-underline">
                {name}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className={`${wrap}`}>
        <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-gap-2 tw-border-t tw-border-[#3a3a3a] tw-py-6 tw-text-[13px] tw-text-[#bdb8ac]">
          <span>Công ty TNHH An Thư The Diamond Store &nbsp;|&nbsp; MST 0314503621</span>
          <span>© {new Date().getFullYear()} An Thư</span>
        </div>
      </div>
    </footer>
  </>
);

/* ------------------------------------------------------------------ */
/* Thẻ bài viết + lưới                                                  */
/* ------------------------------------------------------------------ */

export const articleTag = (a: JournalArticle) => a.press ? 'BÁO CHÍ' : a.news && !a.promotion ? 'TIN AN THƯ' : (a.topics[0] ? TOPIC_BY_SLUG[a.topics[0]].tag : 'TIN TỨC');

export const ArticleCard = ({ a, fit = 'cover', showDate = false }: { a: JournalArticle; fit?: 'cover' | 'poster'; showDate?: boolean }) => (
  <article className="tw-flex tw-flex-col">
    <A href={articleHref(a)} className="tw-block">
      {a.image ? (
        fit === 'poster' ? (
          <div className="tw-flex tw-h-[240px] md:tw-h-[260px] tw-items-start tw-justify-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={a.image} alt={a.title} loading="lazy" className="tw-h-full tw-w-auto tw-max-w-full tw-object-contain" />
          </div>
        ) : (
          /* eslint-disable-next-line @next/next/no-img-element */
          <img
            src={a.image}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
            className={a.press
              ? 'tw-aspect-[16/9] tw-h-auto tw-w-full tw-object-contain tw-bg-jr-band'
              : 'tw-h-[180px] md:tw-h-[190px] tw-w-full tw-object-cover tw-bg-jr-band'}
          />
        )
      ) : (
        <div className={a.press ? 'tw-aspect-[16/9] tw-bg-jr-band' : 'tw-h-[180px] tw-bg-jr-band'} />
      )}
    </A>
    <div className="tw-mt-5">
      {showDate ? (
        <p className="tw-m-0 tw-text-[13px] tw-text-jr-gold">
          <time dateTime={a.date}>{fmtDate(a.date)}</time>
        </p>
      ) : (
        <Eyebrow>{articleTag(a)}</Eyebrow>
      )}
      {a.source && <p className="tw-mt-2 tw-mb-0 tw-text-xs tw-text-jr-mute">Nguồn: <a href={a.source.href} target="_blank" rel="noopener noreferrer" className="hover:tw-underline">{a.source.label} ↗</a></p>}
      <h3 className={`${serif} tw-mt-2 tw-mb-0 tw-text-[20px] md:tw-text-[22px] tw-font-normal tw-leading-[1.3]`}>
        <A href={articleHref(a)} className="tw-text-jr-ink hover:tw-text-jr-green">
          {a.title}
        </A>
      </h3>
      <div className="tw-mt-3">
        <TextLink href={articleHref(a)}>Đọc bài viết</TextLink>
      </div>
    </div>
  </article>
);

export const ArticleGrid = ({ items, fit, showDate }: { items: JournalArticle[]; fit?: 'cover' | 'poster'; showDate?: boolean }) => (
  <div className="tw-grid tw-gap-x-5 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
    {items.map((a) => (
      <ArticleCard key={a.id} a={a} fit={fit} showDate={showDate} />
    ))}
  </div>
);

export const SectionHead = ({ eyebrow = 'GÓC AN THƯ', title, lead }: { eyebrow?: string; title: string; lead?: string }) => (
  <div className="tw-mb-8 md:tw-mb-10">
    <Eyebrow>{eyebrow}</Eyebrow>
    <H2 className="tw-mt-3">{title}</H2>
    {lead && <p className="tw-mt-3 tw-mb-0 tw-text-[17px] tw-text-jr-mute">{lead}</p>}
  </div>
);

export const Section = ({ children, className = '', id }: { children: ReactNode; className?: string; id?: string }) => (
  <section id={id} className={`tw-py-12 md:tw-py-16 ${className}`}>
    <div className={wrap}>{children}</div>
  </section>
);

export const FaqSection = ({ items }: { items: { q: string; a: string }[] }) =>
  items.length ? (
    <Section id="hoi-dap" className="!tw-pt-4">
      <SectionHead title="Câu hỏi thường gặp" />
      <dl className="tw-m-0 tw-grid tw-gap-x-10 tw-gap-y-8 md:tw-grid-cols-3">
        {items.map((f) => (
          <div key={f.q} className="tw-border-t tw-border-jr-line tw-pt-5">
            <dt className={`${serif} tw-text-[20px] tw-leading-snug`}>{f.q}</dt>
            <dd className="tw-m-0 tw-mt-3 tw-text-[16px] tw-leading-relaxed tw-text-jr-mute">{f.a}</dd>
          </div>
        ))}
      </dl>
    </Section>
  ) : null;

const icoProps = { width: 24, height: 24, viewBox: '0 0 24 24', fill: 'none', stroke: '#AF9454', strokeWidth: 1.5, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
const EXPLORE: { slug: string; title: string; sub: string; icon: ReactNode }[] = [
  { slug: 'bang-gia', title: 'Bảng giá', sub: 'Vàng · Ngoại tệ · Kim cương', icon: <svg {...icoProps}><rect x="3" y="13" width="4" height="8" /><rect x="10" y="8" width="4" height="13" /><rect x="17" y="3" width="4" height="18" /></svg> },
  { slug: 'kiem-dinh', title: 'Kiểm định', sub: 'GIA · IGI · Chứng thư', icon: <svg {...icoProps}><rect x="5" y="3" width="14" height="18" rx="1" /><path d="M9 8h6M9 12h6M9 16h4" /></svg> },
  { slug: 'kim-cuong', title: 'Kim cương', sub: '4C · Li & carat', icon: <svg {...icoProps}><path d="M6 3h12l4 6-10 12L2 9z" /><path d="M2 9h20M9 3l-2 6 5 12M15 3l2 6-5 12" /></svg> },
  { slug: 'nhan-cuoi', title: 'Nhẫn cưới', sub: 'Cầu hôn · Đo ni', icon: <svg {...icoProps}><circle cx="9" cy="12" r="6" /><circle cx="15" cy="12" r="6" /></svg> },
  { slug: 'trang-suc', title: 'Trang sức', sub: 'Phong cách · Quà tặng', icon: <svg {...icoProps}><path d="M6 3h12l4 6-10 12L2 9z" /><path d="M2 9h20" /></svg> },
  { slug: 'vang-bao-quan', title: 'Vàng & bảo quản', sub: 'Chất liệu · Chăm sóc', icon: <svg {...icoProps}><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4" /></svg> },
];

export const ExploreMore = ({ exclude }: { exclude?: string }) => {
  const items = EXPLORE.filter((t) => t.slug !== exclude);
  return (
  <Section className="!tw-pt-4">
    <div className="tw-border-0 tw-border-t tw-border-solid tw-border-jr-line tw-pt-8">
      <h2 className={`${serif} tw-m-0 tw-text-[26px] md:tw-text-[32px] tw-font-normal tw-text-jr-ink`}>Khám phá cùng An Thư</h2>
      <div className={`tw-mt-6 tw-grid tw-grid-cols-2 md:tw-grid-cols-3 ${items.length === 5 ? 'lg:tw-grid-cols-5' : 'lg:tw-grid-cols-6'} tw-gap-3 md:tw-gap-4`}>
        {items.map((t) => (
          <A
            key={t.slug}
            href={t.slug === 'bang-gia' ? '/bang-gia/' : topicHref(t.slug)}
            className="tw-group tw-flex tw-flex-col tw-justify-between tw-min-h-[124px] tw-border tw-border-solid tw-border-[#DECA95]/70 tw-bg-white tw-p-4 hover:tw-border-[#AF9454]"
          >
            <span className="tw-flex tw-items-start tw-justify-between">
              {t.icon}
              <span aria-hidden="true" className="tw-text-jr-green">→</span>
            </span>
            <span>
              <span className={`${serif} tw-block tw-text-[19px] tw-leading-tight tw-text-jr-green group-hover:tw-underline`}>{t.title}</span>
              <span className="tw-mt-3 tw-block tw-text-[12px] tw-text-jr-mute">{t.sub}</span>
            </span>
          </A>
        ))}
      </div>
    </div>
  </Section>
  );
};

/** Danh sách bài có nút "Xem thêm" (ẩn/hiện phía client). */
export const ArticleList = ({ items, intro, initial = 6 }: { items: JournalArticle[]; intro?: boolean; initial?: number }) => {
  const first = intro ? initial - 1 : initial;
  const [count, setCount] = useState(first);
  const shown = items.slice(0, count);
  return (
    <>
      <div className="tw-grid tw-gap-x-5 tw-gap-y-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3">
        {intro && (
          <div className="tw-bg-jr-band tw-p-8 tw-self-start">
            <Eyebrow>GÓC AN THƯ</Eyebrow>
            <p className={`${serif} tw-mt-3 tw-mb-0 tw-text-[28px] tw-leading-[1.25]`}>
              Kiến thức
              <br />
              để lựa chọn.
            </p>
          </div>
        )}
        {shown.map((a) => (
          <ArticleCard key={a.id} a={a} />
        ))}
      </div>
      {items.length > count && (
        <div className="tw-mt-10 tw-text-center">
          <button
            type="button"
            onClick={() => setCount((c) => c + 6)}
            className="tw-min-h-[48px] tw-border tw-border-jr-green tw-bg-transparent tw-px-8 tw-text-[15px] tw-font-medium tw-text-jr-green hover:tw-bg-jr-green hover:tw-text-white"
          >
            Xem thêm bài viết
          </button>
        </div>
      )}
      {items.length === 0 && <p className="tw-text-jr-mute">Bài viết sẽ sớm được cập nhật.</p>}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Hero chuyên đề                                                       */
/* ------------------------------------------------------------------ */

const heroBg = { beige: 'tw-bg-jr-band', dark: 'tw-bg-[#0b0b0b] tw-text-white', rose: 'tw-bg-jr-rose', plain: 'tw-bg-jr-bg' };

export const TopicHero = ({ topic, cover }: { topic: Topic; cover?: boolean }) => {
  const h = topic.hero;
  const dark = h.tone === 'dark';
  return (
    <section className={heroBg[h.tone]}>
      <div className={`${wrap} tw-grid tw-items-center tw-gap-8 ${h.image ? 'md:tw-grid-cols-2' : ''} tw-py-10 md:tw-py-14`}>
        <div className="tw-max-w-[520px]">
          <Eyebrow>{h.eyebrow}</Eyebrow>
          <h1 className={`${serif} tw-mt-4 tw-mb-0 tw-text-[34px] md:tw-text-[52px] tw-font-normal tw-leading-[1.15] ${dark ? 'tw-text-white' : 'tw-text-jr-ink'}`}>
            {h.title.map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <br className="tw-hidden md:tw-block" />} {l}
              </React.Fragment>
            ))}
          </h1>
          <p className={`tw-mt-5 tw-mb-0 tw-text-[17px] md:tw-text-[19px] tw-leading-relaxed ${dark ? 'tw-text-[#d7d3c8]' : 'tw-text-jr-mute'}`}>{h.lead}</p>
          {h.cta && (
            <div className="tw-mt-8">
              <Button href={h.cta.href} tone={h.cta.tone}>
                {h.cta.label}
              </Button>
            </div>
          )}
        </div>
        {h.image && (
          <div className="tw-flex tw-justify-center md:tw-justify-end">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={h.image}
              alt={topic.title}
              width={520}
              height={h.imageContain ? 258 : 520}
              className={`tw-w-full tw-max-w-[520px] ${h.imageContain ? 'tw-h-auto tw-bg-white' : 'tw-aspect-square tw-object-cover'}`}
            />
          </div>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Các khối nội dung của trang chuyên đề                                */
/* ------------------------------------------------------------------ */

export const CardGrid = ({ cols, items }: { cols: 3 | 4; items: { title: string; desc?: string; href: string; label?: string }[] }) => (
  <div className={`tw-grid tw-gap-4 sm:tw-grid-cols-2 ${cols === 4 ? 'lg:tw-grid-cols-4' : 'lg:tw-grid-cols-3'}`}>
    {items.map((c) => (
      <div key={c.title} className="tw-bg-jr-band tw-p-6 tw-flex tw-flex-col tw-justify-between tw-min-h-[150px]">
        <div>
          <h3 className={`${serif} tw-m-0 tw-text-[22px] tw-font-normal tw-leading-snug`}>{c.title}</h3>
          {c.desc && <p className="tw-mt-2 tw-mb-0 tw-text-[15px] tw-text-jr-mute">{c.desc}</p>}
        </div>
        <div className="tw-mt-5">
          <TextLink href={c.href}>{c.label || 'Khám phá'}</TextLink>
        </div>
      </div>
    ))}
  </div>
);

export const InstitutionGrid = ({ items }: { items: string[] }) => (
  <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-3">
    {items.map((n) => (
      <div key={n} className="tw-border tw-border-jr-line tw-bg-white tw-p-5">
        <h3 className={`${serif} tw-m-0 tw-text-[24px] tw-font-normal`}>{n}</h3>
        <div className="tw-mt-3">
          <TextLink href={`/tin-tuc/?q=${encodeURIComponent(n)}`}>Tìm hiểu</TextLink>
        </div>
      </div>
    ))}
  </div>
);

export const renderBlock = (
  b: Topic['blocks'][number],
  ctx: { topic: Topic; articles: JournalArticle[]; key: number }
) => {
  const { topic, articles, key } = ctx;
  switch (b.type) {
    case 'cards':
      return (
        <Section key={key} className="!tw-py-8 md:!tw-py-10">
          <CardGrid cols={b.cols} items={b.items} />
        </Section>
      );
    case 'steps':
      return (
        <Section key={key}>
          <SectionHead title={b.title} />
          <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-x-8 tw-gap-y-8">
            {b.items.map((s, i) => (
              <div key={s.en} className="tw-border-b tw-border-jr-line tw-pb-6">
                <p className="tw-m-0 tw-text-[12px] tw-text-jr-gold">{String(i + 1).padStart(2, '0')}</p>
                <h3 className={`${serif} tw-mt-4 tw-mb-0 tw-text-[28px] tw-font-normal`}>{s.en}</h3>
                <p className="tw-mt-3 tw-mb-0 tw-text-[16px] tw-text-jr-mute">{s.vi}</p>
                <div className="tw-mt-4">
                  <TextLink href={s.href}>Tìm hiểu</TextLink>
                </div>
              </div>
            ))}
          </div>
        </Section>
      );
    case 'sizePanel':
      return (
        <Section key={key} className="!tw-pt-0">
          <div className="tw-grid tw-gap-8 md:tw-grid-cols-2 tw-bg-jr-band tw-p-6 md:tw-p-12">
            <div>
              <Eyebrow>KÍCH THƯỚC & TRỌNG LƯỢNG</Eyebrow>
              <H2 className="tw-mt-3 !tw-text-[32px] md:!tw-text-[40px]">Li ↔ carat</H2>
              <p className="tw-mt-4 tw-mb-0 tw-text-[17px] tw-text-jr-mute">Tìm hiểu kích thước và trọng lượng trước khi chọn viên kim cương.</p>
            </div>
            <div className="tw-self-center">
              <div className="tw-grid tw-grid-cols-2 tw-gap-6 tw-border-b tw-border-jr-line tw-pb-5">
                <div>
                  <p className={`${serif} tw-m-0 tw-text-[20px]`}>Li / mm</p>
                  <p className="tw-mt-2 tw-mb-0 tw-text-[15px] tw-text-jr-mute">Kích thước</p>
                </div>
                <div>
                  <p className={`${serif} tw-m-0 tw-text-[20px]`}>Carat</p>
                  <p className="tw-mt-2 tw-mb-0 tw-text-[15px] tw-text-jr-mute">Trọng lượng</p>
                </div>
              </div>
              <p className="tw-mt-5 tw-mb-0 tw-text-[16px] tw-text-jr-mute">Hỏi An Thư để đối chiếu thông số của từng viên.</p>
            </div>
          </div>
        </Section>
      );
    case 'institutions':
      return (
        <Section key={key}>
          <SectionHead title={b.title} />
          <InstitutionGrid items={b.items} />
        </Section>
      );
    case 'readGuide': {
      const first = articles[0];
      return (
        <Section key={key} className="!tw-pt-4">
          <SectionHead title={b.title} lead={b.lead} />
          <div className="tw-grid tw-gap-10 lg:tw-grid-cols-[1.4fr_1fr] tw-items-start">
            <div>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={topic.hero.image} alt="Mẫu giấy kiểm định kim cương" className="tw-w-full tw-h-auto tw-bg-white tw-border tw-border-jr-line" />
              {first && (
                <div className="tw-mt-6">
                  <Button href={articleHref(first)}>{b.cta}</Button>
                </div>
              )}
            </div>
            <ol className="tw-m-0 tw-p-0 tw-list-none">
              {b.steps.map((s, i) => (
                <li key={s.title} className="tw-grid tw-grid-cols-[36px_1fr] tw-gap-3 tw-border-b tw-border-jr-line tw-py-5 first:tw-pt-0">
                  <span className="tw-text-[12px] tw-text-jr-gold tw-pt-2">{String(i + 1).padStart(2, '0')}</span>
                  <div>
                    <h3 className={`${serif} tw-m-0 tw-text-[24px] tw-font-normal`}>{s.title}</h3>
                    <p className="tw-mt-2 tw-mb-0 tw-text-[16px] tw-text-jr-mute">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Section>
      );
    }
    case 'careSteps':
      return (
        <Section key={key} className="!tw-py-8 md:!tw-py-10">
          <div className="tw-border tw-border-jr-line tw-bg-white tw-p-6 md:tw-p-10">
            <H2 className="!tw-text-[26px] md:!tw-text-[34px]">{b.title}</H2>
            <div className="tw-mt-8 tw-grid tw-gap-8 md:tw-grid-cols-3">
              {b.items.map((it, i) => (
                <div key={it.title}>
                  <p className="tw-m-0 tw-text-[12px] tw-text-jr-gold">{String(i + 1).padStart(2, '0')}</p>
                  <h3 className={`${serif} tw-mt-3 tw-mb-0 tw-text-[24px] tw-font-normal`}>{it.title}</h3>
                  <div className="tw-mt-5">
                    <TextLink href={`/tin-tuc/?q=${encodeURIComponent(it.q)}`}>Đọc hướng dẫn</TextLink>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      );
    case 'productLines':
      return (
        <Section key={key}>
          <SectionHead title={b.title} />
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-5 tw-gap-4">
            {b.items.map((p) => (
              <A key={p.title} href={p.href} className="tw-block tw-text-jr-ink">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={p.image} alt={p.title} loading="lazy" className="tw-aspect-square tw-w-full tw-object-cover tw-bg-black" />
                <span className="tw-mt-3 tw-flex tw-items-center tw-justify-between tw-text-[16px]">
                  {p.title}
                  <Arrow />
                </span>
              </A>
            ))}
          </div>
        </Section>
      );
    case 'split':
      return (
        <Section key={key} className="!tw-py-8 md:!tw-py-10">
          <div className="tw-grid tw-gap-8 md:tw-grid-cols-2 tw-items-center">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={b.image} alt={b.title.replace('\n', ' ')} loading="lazy" className="tw-w-full tw-max-w-[550px] tw-aspect-square tw-object-cover tw-bg-black" />
            <div className="md:tw-pl-8">
              <p className="tw-m-0 tw-text-[12px] tw-font-medium tw-uppercase tw-tracking-[0.08em] tw-text-jr-plum">{b.eyebrow}</p>
              <H2 className="tw-mt-4 tw-whitespace-pre-line">{b.title}</H2>
              <p className="tw-mt-4 tw-mb-0 tw-text-[17px] tw-text-jr-mute tw-max-w-[420px]">{b.lead}</p>
              <div className="tw-mt-8">
                <Button href="#bai-viet" tone="plum">
                  {b.cta}
                </Button>
              </div>
            </div>
          </div>
        </Section>
      );
    case 'banner':
      return (
        <Section key={key} className="!tw-py-8 md:!tw-py-10">
          <div
            className={`tw-grid tw-gap-6 md:tw-grid-cols-[1fr_auto] md:tw-items-center tw-p-6 md:tw-p-10 ${b.tone === 'rose' ? 'tw-bg-jr-rose' : 'tw-bg-jr-band'}`}
          >
            <div>
              {b.eyebrow && <Eyebrow>{b.eyebrow}</Eyebrow>}
              <H2 className={`${b.eyebrow ? 'tw-mt-3' : ''} !tw-text-[26px] md:!tw-text-[34px]`}>{b.title}</H2>
              <p className="tw-mt-3 tw-mb-0 tw-text-[17px] tw-text-jr-mute tw-max-w-[560px]">{b.lead}</p>
            </div>
            <Button href={b.href} tone={b.tone === 'rose' ? 'plum' : 'green'}>
              {b.cta}
            </Button>
          </div>
        </Section>
      );
    case 'articles':
      return (
        <Section key={key} id="bai-viet">
          <SectionHead title={b.title} />
          <ArticleList items={articles} intro={b.intro} />
        </Section>
      );
    default:
      return null;
  }
};

export const TopicsGrid = () => (
  <div className="tw-grid tw-gap-4 md:tw-grid-cols-3">
    {JOURNAL_TOPICS.map((t, i) => (
      <A
        key={t.slug}
        href={t.slug === 'bang-gia' ? '/bang-gia/' : topicHref(t.slug)}
        className="tw-group tw-flex tw-flex-col tw-justify-between tw-border tw-border-jr-line tw-bg-white tw-p-5 tw-text-jr-ink hover:tw-border-jr-green"
      >
        <div className="tw-flex tw-items-start tw-gap-5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={t.tile} alt="" loading="lazy" className="tw-h-[88px] tw-w-[88px] tw-shrink-0 tw-object-cover tw-bg-black" />
          <div>
            <p className="tw-m-0 tw-text-[12px] tw-text-jr-gold">{String(i + 1).padStart(2, '0')}</p>
            <h3 className={`${serif} tw-mt-1 tw-mb-0 tw-text-[24px] tw-font-normal tw-leading-tight group-hover:tw-text-jr-green`}>{t.title}</h3>
            <p className="tw-mt-2 tw-mb-0 tw-text-[13px] tw-text-jr-mute">{t.summary}</p>
          </div>
        </div>
        <p className="tw-mt-5 tw-mb-0 tw-text-[14px] tw-font-medium tw-text-jr-green">
          Xem chuyên đề <Arrow />
        </p>
      </A>
    ))}
  </div>
);

export { PAGE_TOPICS };
